import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { creamTokens } from "../../design-system/creamDesignSystem";
import { Icon, type IconName } from "../ui/Icon";
import { getNavigationForRole } from "../../layout/RoleNavigation";

type Command = {
  id: string;
  label: string;
  description: string;
  icon: IconName;
  path: string;
};

export function CommandPalette({ role }: { role?: string }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const nav = useNavigate();
  const commands = useMemo(() => buildCommands(role), [role]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter(command => `${command.label} ${command.description}`.toLowerCase().includes(q));
  }, [commands, query]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(value => !value);
      }
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const run = (command: Command) => {
    nav(command.path);
    setOpen(false);
    setQuery("");
  };

  return (
    <>
      <button type="button" aria-label="Open command palette" onClick={() => setOpen(true)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 14px", background: creamTokens.color.cream, border: `1px solid ${creamTokens.color.line}`, borderRadius: 12, cursor: "pointer", fontSize: 12, color: creamTokens.color.muted, minHeight: 38 }}>
        <Icon name="search" size={16} />
        <span>Command</span>
        <kbd style={{ background: creamTokens.color.line, padding: "1px 5px", borderRadius: 4, fontSize: 10 }}>Ctrl K</kbd>
      </button>
      {open && (
        <div role="dialog" aria-modal="true" aria-label="Command palette" onClick={event => { if (event.target === event.currentTarget) setOpen(false); }} style={{ position: "fixed", inset: 0, zIndex: 2600, background: "rgba(46,36,32,0.32)", display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "10vh" }}>
          <section style={{ width: "min(640px, calc(100vw - 28px))", background: creamTokens.color.surface, borderRadius: 24, boxShadow: "0 28px 90px rgba(46,36,32,0.25)", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 16, borderBottom: `1px solid ${creamTokens.color.line}` }}>
              <Icon name="search" size={20} style={{ color: creamTokens.color.rose }} />
              <input autoFocus value={query} onChange={event => setQuery(event.target.value)} placeholder="Open page or common action..." aria-label="Search commands" style={{ flex: 1, border: 0, outline: 0, fontSize: 16, background: "transparent", color: creamTokens.color.ink }} />
              <button type="button" onClick={() => setOpen(false)} style={{ border: 0, borderRadius: 10, background: creamTokens.color.cream, color: creamTokens.color.muted, padding: "6px 9px", cursor: "pointer" }}>Esc</button>
            </div>
            <div style={{ maxHeight: 430, overflowY: "auto", padding: 8 }}>
              {filtered.map(command => (
                <button key={command.id} type="button" onClick={() => run(command)} style={{ width: "100%", border: 0, background: "transparent", borderRadius: 14, padding: 12, display: "flex", alignItems: "center", gap: 12, textAlign: "left", cursor: "pointer" }}>
                  <span style={{ width: 38, height: 38, borderRadius: 14, display: "grid", placeItems: "center", background: creamTokens.color.roseSoft, color: creamTokens.color.rose }}><Icon name={command.icon} size={18} /></span>
                  <span>
                    <span style={{ display: "block", color: creamTokens.color.ink, fontSize: 13, fontWeight: 900 }}>{command.label}</span>
                    <span style={{ display: "block", color: creamTokens.color.muted, fontSize: 12, marginTop: 2 }}>{command.description}</span>
                  </span>
                </button>
              ))}
              {!filtered.length && <div style={{ padding: 22, textAlign: "center", color: creamTokens.color.muted, fontSize: 13 }}>No commands found.</div>}
            </div>
          </section>
        </div>
      )}
    </>
  );
}

export function buildCommands(role?: string): Command[] {
  const navigationCommands = getNavigationForRole(role).map(([path, label, icon]) => ({
    id: `open-${path}`,
    label: `Open ${label}`,
    description: `Navigate to ${label}`,
    icon: icon as IconName,
    path,
  }));
  const hasOrders = navigationCommands.some(command => command.path === "/orders");
  return [
    ...navigationCommands,
    ...(hasOrders ? [
      { id: "create-inquiry", label: "Create Inquiry", description: "Open sales workspace to create an inquiry", icon: "plus" as IconName, path: "/orders" },
      { id: "create-order", label: "Create Order", description: "Open sales workspace to create an order", icon: "shopping-bag" as IconName, path: "/orders" },
    ] : []),
  ];
}
